export interface CustomControlType {
  name: string;
  key: string;
  description: string;
  version?: 'v1' | 'v2';
}

export const CustomControlTypes: CustomControlType[] = [
  {
    name: 'Listing Status',
    key: 'ListingStatusField',
    description:
      "A control for entering listing status values. Typically there's only one status selector on a form.",
  },
  {
    name: 'Brokerage Participants',
    key: 'BrokerageParticipants',
    description:
      'Participants on a listing that belong to the brokerage and can be found in the system.',
    version: 'v2',
  },
  {
    name: 'Client Participants',
    key: 'ClientParticipants',
    description:
      'Participants on a listing that belong outside the brokerage but can be added to the listing.',
  },
  {
    name: 'Listing Label',
    key: 'ListingIdPropertyType',
    description: 'A label containing the listing id and property type',
  },
  {
    name: 'Multi Select',
    key: 'MultiSelectField',
    description: 'A type of option selection for multiple values',
    version: 'v2',
  },
  {
    name: 'Owner Details',
    key: 'OwnerDetails',
    description: 'A plain text input for the owner information on the listing',
  },
  {
    name: 'Date Picker',
    key: 'DatePicker',
    description:
      'A control for selecting a date, typically used for date fields in the listing.',
  },
  {
    name: 'Currency Field',
    key: 'CurrencyField',
    description:
      'A control for entering currency values, typically used for price fields in the listing.',
  },
  {
    name: 'Media Center Photos',
    key: 'Photos',
    description: 'Photos of the property',
  },
  {
    name: 'Media Center Documents',
    key: 'Documents',
    description: 'Documents for the listing',
  },
  {
    name: 'Media Center Virtual Tours',
    key: 'Tours',
    description: 'Tours for the listing',
  },
  {
    name: 'Conditional Section',
    key: 'ConditionalSection',
    description:
      'Render a section based on the visibility of another field. Provide the rule as the key.',
  },
  {
    name: 'Return To Top',
    key: 'ReturnToTop',
    description: 'A widget to return the form to the top.',
  },
  {
    name: 'Open Houses',
    key: 'OpenHouse',
    description:
      'All the open houses for the listing. CRUD operations to control open houses. Set Key to PAST to show past open houses',
  },
  {
    name: 'AI Generated Remarks',
    key: 'GeneratedRemarks',
    description: 'Attached our AI generated remarks to a field',
  },
  {
    name: 'Text Area',
    key: 'TextArea',
    description:
      'A large text area for text content that can not fit in a standard text input.',
  },
  {
    name: 'Time Picker',
    key: 'TimePicker',
    description: 'A time selection control.',
  },
  {
    name: 'Text Blurb/Description',
    key: 'TextBlurb',
    description: 'Some read only text on the form for informational purposes.',
  },
  {
    name: 'Value Display',
    key: 'ValueDisplay',
    description: 'A label and field value as a read only field.',
  },
  {
    name: 'Percent Field',
    key: 'PercentField',
    description: 'A control for providing percent fields.',
  },
  {
    name: 'Radio Button Group',
    key: 'RadioButtonGroup',
    description: 'Present multiple fields as a radio group list.',
  },
  {
    name: 'Clearable Text Field',
    key: 'ClearableTextField',
    description:
      'A text field with a clear button, helpful for auto generated fields that can be removed.',
  },
  {
    name: 'Measurements Calculations',
    key: 'MeasurementsCalculations',
    description: 'A summary panel for important measurements calculations.',
  },
  {
    name: 'Green Verification',
    key: 'GreenVerificationControl',
    description: "Manage a listing's green verification records.",
  },
  {
    name: 'Year Picker',
    key: 'YearPicker',
    description: 'A control to input years.',
  },
  {
    name: 'Parking Summary',
    key: 'ParkingSummary',
    description:
      'A summary of all parking spaces and parking features on the listing.',
  },
  {
    name: 'Tax ID Search',
    key: 'TaxSearch',
    description:
      'Search tax records in the public records database by address, mls id, or text.',
  },
  {
    name: 'Location Map',
    key: 'LocationMap',
    description:
      'Show the current listing location on a map, also drag the pin to select a new location.',
  },
  {
    name: 'School Finder',
    key: 'SchoolInfo',
    description:
      'Search and find schools for the listing, scoped to the listings location.',
  },
  {
    name: 'Postal City Search',
    key: 'SearchPostalCity',
    description: 'Smart city searches based on the current listing location.',
  },
  {
    name: 'County Search',
    key: 'SearchCounty',
    description: 'Smart county searches based on the current listing location.',
  },
  {
    name: 'Dynamic Lookup Search',
    key: 'DynamicLookup',
    description:
      'Dynamic lookup search that queries the SaaS API based on parent field value.',
    version: 'v2',
  },
  {
    name: 'Postal Code Search',
    key: 'SearchPostalCode',
    description:
      'Smart postal code searches based on the current listing location.',
  },
  {
    name: 'Subdivision Search',
    key: 'SearchSubdivision',
    description:
      'Smart subdivision searches based on the current listing location.',
  },
  {
    name: 'Building Search',
    key: 'SearchBuilding',
    description:
      'Smart building searches based on the current listing location.',
  },
  {
    name: 'School Search',
    key: 'SearchSchool',
    description: 'Smart school searches based on the current listing location.',
  },
  {
    name: 'School District Search',
    key: 'SearchSchoolDistrict',
    description:
      'Smart school district searches based on the current listing location.',
  },
  {
    name: 'MLS Area Search',
    key: 'SearchArea',
    description:
      'Smart MLS area searches based on the current listing location.',
  },
  {
    name: 'Office Exclusive Terms of Use',
    key: 'OETerms',
    description: 'A control to accept the terms of office exclusive listings.',
  },
  {
    name: 'RentSpree Toggle',
    key: 'RentSpreeRadioButtonGroup',
    description: 'A control opt in to rent spree lease link generation.',
  },
  {
    name: 'RentSpree Lease Link',
    key: 'RentSpreeLeaseLinkDisplay',
    description: 'Manage rentspree generated links.',
  },
  {
    name: 'Lot Size SQFT',
    key: 'LotSQFT',
    description:
      'Allows users to input lot size in square feet and update acres values dynamically.',
  },
  {
    name: 'Lot Size Acres',
    key: 'LotSizeAcres',
    description:
      'Allows users to input lot size in acres and update sqft values dynamically.',
  },
  {
    name: 'Showing Choice',
    key: 'ShowingChoice',
    description:
      'Allows users to select showing provider for unpublished listings with listing-level config',
  },
  {
    name: 'Showing Management Button',
    key: 'ManageShowingsButton',
    description:
      "Calls the vendor showing lambda to get the external link to the listing's showing provider",
    version: 'v2',
  },
  {
    name: 'Unit',
    key: 'Unit',
    description: 'Manage Units for Multi Family and Commercial Properties',
    version: 'v2',
  },
  {
    name: 'LevelsAndRooms',
    key: 'LevelsAndRooms',
    description: 'Manage Levels and Rooms',
    version: 'v2',
  },
  {
    name: 'Co List Agent',
    key: 'CoListAgent',
    description:
      'Co-List Agent broken out of BrokerageParticipants for use with Set Field Defaults',
    version: 'v2',
  },
  {
    name: 'Auto Hide Off Market Media Warning',
    key: 'AutoHideOffMarketMediaWarning',
    description:
      'Warning that shows if Auto Hide Off Market Media is true, but no photo is marked to be saved',
    version: 'v2',
  },
  {
    name: 'Office Exclusive Document',
    key: 'OfficeExclusiveDocument',
    description: 'Upload Office Exlusive Document',
    version: 'v2',
  },
  {
    name: 'Parcel Number',
    key: 'ParcelNumber',
    description:
      'A parcel number input with county-specific format validation for Ohio counties.',
  },
  {
    name: 'Lock Box Management Button',
    key: 'ManageLockBox',
    description:
      'Provides buttons to manage Supra or SentriLock lock boxes based on the selected lock box type',
    version: 'v2',
  },
  {
    name: 'Input Ignore Errors',
    key: 'InputIgnoreErrors',
    description: 'A text input that suppresses error display',
  },
  {
    name: 'LevelsAndRoomsNoMain',
    key: 'LevelsAndRoomsNoMain',
    description: 'Manage Rooms without level hierarchy',
    version: 'v2',
  },
];
